/**
 * The body of an expanded table row (DESIGN.md "Data table", D25): two or
 * three columns (1.1 / 1.3 / 0.9fr, 32px apart) that stack below 1100px,
 * then an optional full-width button row. DataTable's detail row supplies the
 * layer-02 ground and the 64px indent. RowDetailSection is one titled block
 * ("What changed"); ButtonSet is Carbon's equal-width button set (1px gaps,
 * 320px minimum, stacking when narrow) for the actions inside a column.
 */
import type { ReactNode } from 'react'
import clsx from 'clsx'

export interface RowDetailProps {
  columns: [ReactNode, ReactNode, ReactNode?]
  /** A full-width row of buttons under every column. */
  actions?: ReactNode
  className?: string
}

export function RowDetail({ columns, actions, className }: RowDetailProps) {
  const [a, b, c] = columns
  return (
    <div
      className={clsx(
        'grid gap-8 max-[1100px]:grid-cols-1 max-[1100px]:gap-6',
        c ? 'grid-cols-[minmax(0,1.1fr)_minmax(0,1.3fr)_minmax(0,0.9fr)]' : 'grid-cols-[minmax(0,1.1fr)_minmax(0,1.3fr)]',
        className,
      )}
    >
      <div className="min-w-0">{a}</div>
      <div className="min-w-0">{b}</div>
      {c ? <div className="min-w-0">{c}</div> : null}
      {actions ? <div className="col-span-full flex min-w-0 flex-wrap">{actions}</div> : null}
    </div>
  )
}

export interface RowDetailSectionProps {
  title: ReactNode
  /** Heading level inside the table (the table title is an h2). */
  as?: 'h3' | 'h4'
  children: ReactNode
  className?: string
}

/** A titled block in a detail column; consecutive sections sit 24px apart. Bold runs read in primary ink. */
export function RowDetailSection({ title, as: Heading = 'h3', children, className }: RowDetailSectionProps) {
  return (
    <section className={clsx('min-w-0 [&+&]:mt-6', className)}>
      <Heading className="mb-2 text-[14px] font-semibold leading-[1.29] text-text-primary">{title}</Heading>
      <div className="text-body text-text-secondary [&_b]:font-semibold [&_b]:text-text-primary">{children}</div>
    </section>
  )
}

export interface ButtonSetProps {
  children: ReactNode
  className?: string
}

/** Carbon button set: equal widths in a 1px-gap grid, stacking full width when the column is narrow. */
export function ButtonSet({ children, className }: ButtonSetProps) {
  return (
    <div className={clsx('grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-px [&>*]:w-full [&>*]:max-w-none', className)}>
      {children}
    </div>
  )
}

export default RowDetail
